/* ==============================
 Bilmedie – Nummerplade-søger – v6.7 (DK + SE + EN i18n)
 - BMW support: "318I MJ00" → 3-serie + E46 (begge kandidater)
 - Fjernelse af årskode-token "MJ00/MJ01/..." (og generisk mj\d\d)
 - Flere BMW-chassis-koder (E30/E34/E36/E39/E46/E60/E61/E87/E90/E91/E92/F30/F31/G20 …) tilføjes som handles
 - Make-specifikke aliaser (fra v6.6 beholdt) + forbedret base-normalisering
 - Performance: strammere timeouts, færre URL-forsøg pr. handle, aggressiv parallelisering + memo-caches
 - Beholder v6.5 SE-performance (retries, breaker, plate-cache)
============================== */

/* ---------------------------
  Landekode-resolver (badge → domæne)
---------------------------- */
function resolveCountryCode(badgeEl) {
  const raw = (badgeEl?.dataset?.country || badgeEl?.textContent || '')
    .trim().toUpperCase();
  if (raw === 'DK' || raw === 'SE') return raw;
  const host = (location.hostname || '').toLowerCase();
  if (host.endsWith('.se')) return 'SE';
  return 'DK';
}

/* ---------------------------
  Country dropdown (flag + kode)
---------------------------- */
function countrySelectDropdown() {
  const flagContainer = document.querySelector('#number-plate-form .input-wrapper');
  if (!flagContainer) return;
  const countryCodeElement = flagContainer.querySelector('.country-code');
  const countryDropdown = flagContainer.querySelector('.country-dropdown');
  const toggle = flagContainer.querySelector('.eu-flag-container');
  if (!toggle || !countryCodeElement || !countryDropdown) return;

  toggle.addEventListener('click', () => {
    countryDropdown.style.display = countryDropdown.style.display === 'block' ? 'none' : 'block';
    countryCodeElement.classList.toggle('open');
  });

  countryDropdown.querySelectorAll('li').forEach((item) => {
    item.addEventListener('click', () => {
      const code = (item.getAttribute('data-country') || '').toUpperCase(); // "DK"/"SE"
      countryCodeElement.textContent = code;
      countryCodeElement.dataset.country = code;
      countryDropdown.style.display = 'none';
      countryCodeElement.classList.remove('open');
    });
  });

  const defaultCode = resolveCountryCode(countryCodeElement);
  countryCodeElement.textContent = defaultCode;
  countryCodeElement.dataset.country = defaultCode;
}

/* ---------------------------
  Loader styles
---------------------------- */
function ensureLoaderStyles(){
  if (document.getElementById('bm-loader-style')) return;
  const s = document.createElement('style');
  s.id = 'bm-loader-style';
  s.textContent = `
  .bm-loading{display:inline-flex;align-items:center;gap:.5rem;opacity:.9}
  .bm-loading__spinner{width:1em;height:1em;border:.2em solid currentColor;border-right-color:transparent;border-radius:50%;animation:bmspin .8s linear infinite}
  @keyframes bmspin{to{transform:rotate(360deg)}}`;
  document.head.appendChild(s);
}

/* ---------------------------
  i18n (DA/SV/EN efter domæne)
---------------------------- */
const __HOST__ = (location.hostname || '').toLowerCase();
const IS_DK = __HOST__.endsWith('.dk');
const IS_SE = __HOST__.endsWith('.se');
const IS_EN = __HOST__.includes('carmediaeurope.com');

const I18N = IS_SE ? {
  labelMake: 'Märke',
  labelModel: 'Modell',
  loading: 'Hämtar produkter…',
  enterPlate: 'Ange ett registreringsnummer.',
  lookupError: 'Fel vid uppslagning av registreringsnummer.',
  countryUnsupported: 'Landet stöds inte ännu.',
  noVehicle: 'Ingen fordonsdata hittades.',
  unknown: 'Okänd',
  usingCache: 'Visar resultat från cache på grund av API-fel.'
} : IS_EN ? {
  labelMake: 'Make',
  labelModel: 'Model',
  loading: 'Fetching products…',
  enterPlate: 'Enter a license plate.',
  lookupError: 'Error looking up license plate.',
  countryUnsupported: 'Country not supported yet.',
  noVehicle: 'No vehicle data found.',
  unknown: 'Unknown',
  usingCache: 'Showing cached results due to API error.'
} : {
  labelMake: 'Mærke',
  labelModel: 'Model',
  loading: 'Henter produkter…',
  enterPlate: 'Indtast en nummerplade.',
  lookupError: 'Fejl ved opslag af nummerplade.',
  countryUnsupported: 'Landet er ikke understøttet endnu.',
  noVehicle: 'Ingen køretøjsdata fundet.',
  unknown: 'Ukendt',
  usingCache: 'Viser cachede resultater pga. API-fejl.'
};

/* ---------------------------
  Hjælpere: handleize + model-normalisering
---------------------------- */
function handleize(str) {
  return (str || '')
    .toString()
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

/* Make-specifikke aliaser (platform → base-model) */
const MAKE_CODE_ALIASES = {
  'audi': {
    '4g': 'a6', '4f': 'a6', 'c7': 'a6',
    '8v': 'a3', '8p': 'a3',
    'b8': 'a4', 'b9': 'a4'
  },
  'ford': {
    'bwy': 'mondeo', 'b5y': 'mondeo'
  },
  'hyundai': {
    'pa': 'i10', 'pb': 'i20', 'gb': 'i20', 'gd': 'i30', 'fd': 'i30'
  },
  'kia': {
    'ed': 'ceed', 'jd': 'ceed', 'cd': 'ceed'
  },
  'volkswagen': {
    '1k': 'golf', '5g': 'golf'
  },
  'bmw': {
    // BMW E/F/G-chassiskoder → serie-gæt (bruges KUN som hint til base; vi tilføjer både serie og chassis som kandidater)
    'e30': '3-serie','e36': '3-serie','e46': '3-serie','e90': '3-serie','e91': '3-serie','e92': '3-serie',
    'f30': '3-serie','f31': '3-serie','g20': '3-serie','g21': '3-serie',
    'e34': '5-serie','e39': '5-serie','e60': '5-serie','f10': '5-serie','g30': '5-serie'
  },
  'citroën': {}, 'citroen': {}
};

/**
 * Grund-normalisering af model (make-uafhængig):
 * - Fjerner parenteser, romertal, mk/b/f-koder, 3-tegns [a-z][0-9][a-z]
 * - Fjerner karrosseri/trim/fuel mm. (turnier/variant/allroad/gt/tsi170 osv.)
 * - Fjerner årskode "mj00/mj01/..." tokens
 */
function normalizeModelLabel(modelLabel){
  const stop = new Set([
    'allroad','avant','touring','estate','wagon','combi','kombi','sw','sportswagon','stationcar','break',
    'sedan','hatchback','hb','compact','coupe','cabrio','cabriolet','roadster','turnier','variant','cross','country',
    'gt','gti','gtd','gts','rs','sline','line','sport','sportline','m','amg','nline','st','vrs',
    'gtline','gt-line','gt sport','gtsport',
    'mhev','hev','phev','ev','plugin','plug-in','hybrid','quattro','xdrive','4motion',
    'dci','tdi','tsi','tfsi','cdti','hdi','skyactiv','t-gdi','tgdi','gdi','multijet','bluehdi','ecoboost',
    'lx','ex','sx','comfort','premium','active','style','trend','advance','business','collection','edition',
    '5-dørs','5dørs','5-dors','5dors','3-dørs','3dørs','3-dors','3dors','5d','3d','5','3'
  ]);

  let t = (modelLabel||'').toString().toLowerCase().replace(/['’`´]/g, '');
  t = t.replace(/\([^)]*\)/g, ' ');
  t = t.replace(/\b(mk\s*[0-9ivx]+|[ivx]{1,4}|b[0-9]{1,2}|f[0-9]{2})\b/gi, ' ');
  t = t.replace(/\b[a-z]\d[a-z]\b/gi, ' ');
  // Fjern årskode "mj00/mj01/mj02..."
  t = t.replace(/\bmj\d{2}\b/gi, ' ');
  t = t.replace(/[^a-z0-9]+/g,' ').trim();
  if (!t) return (modelLabel||'').toString();

  let words = t.split(/\s+/).filter(Boolean);
  // Fjern motor-hp tokens: "tsi 170", "hdi110"
  words = words.map(w => w.replace(/(hdi|tdi|tsi|tfsi|gdi|tgdi|dci)(\d{2,3})$/, '$1'));
  words = words.filter(w => !/^\d{2,3}$/.test(w));
  words = words.filter(w => !stop.has(w));
  if (!words.length) return (modelLabel||'').toString();

  return words.join(' ');
}

/**
 * Make-aware base-model:
 * - Strip indledende chassis-token (2–3 bogstaver) før model
 * - Make-specifik alias (AUDI 4G→A6, HYUNDAI PA→i10, FORD BWY→Mondeo, KIA ED→Ceed)
 * - BMW: genkender "318i/320d/…": mapper til serie (3-serie/5-serie osv.) + husker chassis (E46/E90…) hvis til stede
 *   Returnerer base string (fx "3-serie") – men vi bruger både serie og chassis som kandidater i buildCandidateCollectionHandles()
 */
function normalizeToBaseModel(makeLabel, modelLabel){
  const makeRaw = (makeLabel||'').toString();
  const make = makeRaw.toLowerCase().trim().replace(/[^a-zäöüßøåéèáíóúç-]/g,'');
  let core = normalizeModelLabel(modelLabel||'').toLowerCase().trim();
  if (!core) return (modelLabel||'').toString().toLowerCase();

  let words = core.split(/\s+/).filter(Boolean);

  // Strip indledende 2–3 bogstaver hvis efterfulgt af "rigtig" model
  if (words.length >= 2 && /^[a-z]{2,3}$/.test(words[0]) &&
      (/^[a-z]\d{1,3}[a-z]?$/.test(words[1]) || /^[a-z]{3,}$/.test(words[1]))) {
    words.shift();
  }

  // Kandidat base
  let base = words[0];
  for (const w of words) {
    if (/^[a-z]\d{1,3}[a-z]?$/.test(w) || /^[a-z]{3,}$/.test(w)) { base = w; break; }
    if (/^\d{2,3}$/.test(w)) continue;
    base = w;
  }

  // Make-specifik mapping (platformskode→model)
  const aliasMap =
    MAKE_CODE_ALIASES[make] ||
    MAKE_CODE_ALIASES[make.replace('é','e')] ||
    {};
  if (aliasMap[base]) base = aliasMap[base];

  // KIA CEE'D
  if (base === 'ceedsw' || base === 'ceed') base = 'ceed';

  // BMW: detekter små-modelkoder (316i, 318i, 320d, 520i …) og mapp til serie
  if (make === 'bmw') {
    // E/F/G-koder i input?
    const chassis = words.find(w => /^[efg]\d{2}$/.test(w));
    // Motor/trim nummer→serie (3xx -> 3-serie, 5xx -> 5-serie, 1xx -> 1-serie osv.)
    const numTrim = words.find(w => /^\d{3}[a-z]?$/.test(w)); // fx 318i, 520d
    if (numTrim) {
      const firstDigit = numTrim.charAt(0);
      if (firstDigit === '1') base = '1-serie';
      else if (firstDigit === '2') base = '2-serie';
      else if (firstDigit === '3') base = '3-serie';
      else if (firstDigit === '4') base = '4-serie';
      else if (firstDigit === '5') base = '5-serie';
      else if (firstDigit === '6') base = '6-serie';
      else if (firstDigit === '7') base = '7-serie';
      // vi bevarer chassis separat i kandidatlisten
      if (chassis) base = base; // no-op (chassis tilføjes senere som kandidat)
    } else if (chassis && aliasMap[chassis]) {
      // hvis kun chassis, brug alias hint (typisk 3-serie/5-serie)
      base = aliasMap[chassis];
    }
  }

  if (/^\d$/.test(base) && words[1] === 'serie') return `${base} serie`;
  return base;
}

/* ---------------------------
  Grid caches
---------------------------- */
const __GRID_MEMO__ = new Map(); // handle → HTML
const __NEG_MEMO__  = new Map(); // handle → ts
const __PAIR_MEMO__ = new Map(); // (make|model) → HTML
const GRID_CACHE_TTL_MS = 1000 * 60 * 60 * 6; // 6 timer
const NEG_CACHE_TTL_MS  = 1000 * 60 * 30;     // 30 min
function cacheKey(handle){ return `bm:grid:${handle}`; }
function pairKey(make, model){ return `bm:pair:${make}::${model}`; }
function now(){ return Date.now(); }

function getCachedGrid(handle){
  const neg = __NEG_MEMO__.get(handle);
  if (neg && now() - neg < NEG_CACHE_TTL_MS) return null;
  if (__GRID_MEMO__.has(handle)) return __GRID_MEMO__.get(handle);
  try{
    const raw = sessionStorage.getItem(cacheKey(handle));
    if (!raw) return null;
    const { html, ts } = JSON.parse(raw);
    if (!html || !ts) return null;
    if (now() - ts > GRID_CACHE_TTL_MS) { sessionStorage.removeItem(cacheKey(handle)); return null; }
    __GRID_MEMO__.set(handle, html); return html;
  }catch(_){ return null; }
}
function setCachedGrid(handle, html){
  if (!html) return;
  __GRID_MEMO__.set(handle, html);
  try{ sessionStorage.setItem(cacheKey(handle), JSON.stringify({ html, ts: now() })); }catch(_){ }
}
function setNegativeCache(handle){ __NEG_MEMO__.set(handle, now()); }

/* ---------------------------
  Produktgrid-udtræk + hentning
---------------------------- */
function pickProductGrid(doc) {
  return doc.querySelector(
    'ul#main-collection-product-grid, ul#product-grid, .productGrid, #CollectionProductGrid, .collection .product-grid, .collection [data-product-grid]'
  );
}
// Strammere set for færre runder
const SECTION_IDS = ['main-collection-product-grid','collection-product-grid','product-grid'];
const VIEWS = ['grid','products',''];

function fetchWithTimeout(url, opts = {}, ms = 3000) {
  const controller = new AbortController();
  const t = setTimeout(() => controller.abort('timeout'), ms);
  const merged = { ...opts, signal: controller.signal, credentials: 'same-origin' };
  return fetch(url, merged).finally(() => clearTimeout(t));
}
async function parseGridFromHTML(html){
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const grid = pickProductGrid(doc) || doc.body;
  if (!grid) return null;
  if (grid.querySelector('[data-product-id], li, .card, product-card') || grid.children.length) return grid.outerHTML;
  return null;
}
async function fetchCollectionGridByHandle(handle) {
  if (!handle) return null;
  const cached = getCachedGrid(handle); if (cached) return cached;

  // Kun de mest lovende URL’er først (hurtigere)
  const urls = [
    `/collections/${handle}?section_id=${encodeURIComponent('main-collection-product-grid')}`,
    `/collections/${handle}?section_id=${encodeURIComponent('collection-product-grid')}`,
    `/collections/${handle}`,
    `/collections/${handle}?view=grid`
  ];

  try {
    const firstHit = await Promise.any(urls.map(async (u)=>{
      const res = await fetchWithTimeout(u, {}, 3000);
      if (!res.ok) throw new Error('bad');
      const html = await res.text();
      const grid = await parseGridFromHTML(html);
      if (!grid) throw new Error('nogrid');
      return grid;
    }));
    if (firstHit) { setCachedGrid(handle, firstHit); return firstHit; }
  } catch (_) {}

  setNegativeCache(handle);
  return null;
}

/* ---------------------------
  BMW-chassis liste til kandidater
---------------------------- */
const BMW_CHASSIS = [
  'e30','e34','e36','e39','e46','e60','e61','e87','e90','e91','e92','f10','f30','f31','g20','g21'
];

/* ---------------------------
  Kandidater til kollektions-handles
---------------------------- */
function buildCandidateCollectionHandles(make, model) {
  const mke = handleize(make);
  const rawLabel = (model || '').trim();
  const base = normalizeToBaseModel(make, model);
  const mdl = handleize(base);

  const candidates = [
    `${mke}-${mdl}`, // audi-a6, hyundai-i10, ford-mondeo, bmw-3-serie
    mdl,
    rawLabel.toLowerCase().replace(/\s+/g, '-')
  ];

  // BMW: tilføj serie + relevante chassis-handle(s)
  if (mke === 'bmw') {
    // Tilføj 1–7-serie alternativer hvis base er en af dem
    const plain = mdl.replace(/-/g, '');
    const digitSeries = plain.match(/^([1-7])serie$/);
    if (digitSeries) {
      const s = digitSeries[1];
      candidates.push(`${s}-serie`, `bmw-${s}-serie`, `${s}-serie-compact`, `bmw-${s}-serie-compact`);
    }

    // Uddrag chassis fra original label og base
    const labelTokens = (rawLabel.toLowerCase().match(/[a-z0-9]+/g) || []);
    const chassisTokens = new Set(
      labelTokens.filter(w => /^[efg]\d{2}$/.test(w)).concat(BMW_CHASSIS.filter(c => base.includes(c)))
    );

    // Tilføj E/F/G-chassis som egne kandidater
    [...chassisTokens].forEach(ch => {
      candidates.unshift(ch, `bmw-${ch}`);
      // Map også chassis til serie-alias hvis kendt
      const hint = (MAKE_CODE_ALIASES['bmw'] || {})[ch];
      if (hint) {
        const h = handleize(hint);
        candidates.unshift(h, `bmw-${h}`);
      }
    });

    // Hvis base ligner 3xx/5xx trim (fx 318i), sørg for serie-kandidater
    if (/^\d-serie$/.test(mdl) || /^\d{3}[a-z]?$/.test(rawLabel.replace(/\s+/g,''))) {
      const firstDigit = rawLabel.trim()[0];
      if (/[1-7]/.test(firstDigit)) {
        candidates.unshift(`${firstDigit}-serie`, `bmw-${firstDigit}-serie`);
      }
    }
  }

  // Mercedes: "-klasse/-class"
  if (mke === 'mercedes-benz' || mke === 'mercedes') {
    candidates.push(`${mdl}-klasse`, `${mdl}-class`, `mercedes-${mdl}-klasse`, `mercedes-${mdl}-class`);
  }

  // VW Beetle aliaser
  if (mke === 'volkswagen' && (/\bbeetle\b/i.test(base) || mdl === 'beetle')) {
    ['beetle','new-beetle','the-beetle','volkswagen-beetle','volkswagen-new-beetle','volkswagen-the-beetle']
      .forEach(h => candidates.unshift(h));
  }

  return [...new Set(candidates.filter(Boolean))];
}

/* ---------------------------
  Filtreret søgning (fallback)
---------------------------- */
function baseModelTokens(make, model) {
  let m = (make || '').toLowerCase();
  let mdl = normalizeToBaseModel(make, model).toLowerCase();
  mdl = mdl.normalize('NFD').replace(/[\u0300-\u036f]/g,'')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
  const raw = mdl.split(/\s+/).filter(Boolean);
  const tokens = raw.filter(t => !/^[a-z][0-9]$/i.test(t));
  return { make: m, tokens };
}
function strictTokenHit(text, tok){
  if (/^i\d+$/.test(tok)) return new RegExp('(?:^|[\\s-])' + tok + '(?:$|[\\s-])').test(text);
  return text.includes(tok);
}
async function fetchSearchGridFiltered(make, model) {
  const { make: m, tokens } = baseModelTokens(make, model);
  const query = [make, model].filter(Boolean).join(' ');
  const negativeTest = (t) => {
    if (m === 'kia' && /\brio\b/.test(t) && tokens.includes('picanto')) return true;
    if (m === 'honda' && /\bc5\b/.test(t)) return true;
    if (m === 'hyundai' && /\bi1(0|20|30)\b/.test(t)) return true;
    return false;
  };
  try {
    const res = await fetch(`/search?q=${encodeURIComponent(query)}&type=product`, { credentials: 'same-origin' });
    if (!res.ok) return null;
    const html = await res.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const grid = pickProductGrid(doc) || doc.querySelector('.search-results, .template-search');
    if (!grid) return null;
    const items = Array.from(grid.querySelectorAll('li, .card, .grid__item'));
    let kept = 0;
    items.forEach(li => {
      const text = li.textContent.toLowerCase();
      const makeHit = m ? text.includes(m) : true;
      const allTokensHit = tokens.every(tok => strictTokenHit(text, tok));
      const neg = negativeTest(text);
      if (!(makeHit && allTokensHit && !neg)) li.remove(); else kept++;
    });
    if (!kept) return null;
    return grid.outerHTML;
  } catch(_) { return null; }
}

/* ---------------------------
  BMW-serie helper (kun BMW)
---------------------------- */
async function trySeriesCollectionGrid(make, model) {
  const mke = handleize(make);
  if (mke !== 'bmw') return null;
  const mdl = handleize(normalizeToBaseModel(make, model)).replace(/-/g, '');
  if (/^(1|2|3|4|5|6|7)serie$/.test(mdl)) {
    const s = mdl.charAt(0);
    const h1 = `${s}-serie`;
    const h2 = `${s}-serie-compact`;
    const grid1 = await fetchCollectionGridByHandle(h1);
    if (grid1) return grid1;
    const grid2 = await fetchCollectionGridByHandle(h2);
    if (grid2) return grid2;
  }
  return null;
}

/* ---------------------------
  Vis relevante produkter (core)
---------------------------- */
const __GRID_PAIR_MEMO__ = new Map(); // (make|model) → HTML
let __inflightController__;
async function showRelevantProducts(carMake, carModel) { console.log("carMake=>"+carMake,"carModel=>"+carModel);
  if (__inflightController__) { try{ __inflightController__.abort('superseded'); } catch(_){} }
  __inflightController__ = new AbortController();

  ensureLoaderStyles();
  const carInfoContainer = document.getElementById('car-info');
  const productResultsContainer = document.getElementById('product-results');

  const make = (carMake || '').toString().trim().replace(/,+$/, '');
  const model = (carModel || '').toString().trim();

  if (carInfoContainer) carInfoContainer.innerHTML =
    `<h4>${I18N.labelMake}: ${make || I18N.unknown} | ${I18N.labelModel}: ${model || I18N.unknown}</h4>`;
  if (productResultsContainer) productResultsContainer.innerHTML =
    `<div class="bm-loading" aria-busy="true"><span>${I18N.loading}</span><span class="bm-loading__spinner" aria-hidden="true"></span></div>`;

  const pKey = pairKey(make, model);
   console.log("pKey=>"+pKey);
  const memo = __GRID_PAIR_MEMO__.get(pKey);
  if (memo) { productResultsContainer.innerHTML = memo; return; }

  const handles = buildCandidateCollectionHandles(make, model);

  try {
    const first = await Promise.any(handles.slice(0, 4).map(h => fetchCollectionGridByHandle(h)));
    if (first) { __GRID_PAIR_MEMO__.set(pKey, first); productResultsContainer.innerHTML = first; return; }
  } catch(_) {}

  for (let i=4; i<handles.length; i+=4) {
    try {
      const hit = await Promise.any(handles.slice(i, i+4).map(h => fetchCollectionGridByHandle(h)));
      if (hit) { __GRID_PAIR_MEMO__.set(pKey, hit); productResultsContainer.innerHTML = hit; return; }
    } catch(_) {}
  }

  const seriesGrid = await trySeriesCollectionGrid(make, model);
  if (seriesGrid) { __GRID_PAIR_MEMO__.set(pKey, seriesGrid); productResultsContainer.innerHTML = seriesGrid; return; }

  const searchGrid = await fetchSearchGridFiltered(make, model);
  if (searchGrid) { __GRID_PAIR_MEMO__.set(pKey, searchGrid); productResultsContainer.innerHTML = searchGrid; return; }

  tryFallbackSearch(make, model);
}

/* ---------------------------
  Serien/faste regler fallback (KUN BMW)
---------------------------- */
function tryFallbackSearch(carMake, carModel) {
  const productResults = document.getElementById('product-results');
  const make = handleize(carMake);
  if (make !== 'bmw') {
    if (productResults) productResults.innerHTML = `<p>${I18N.noVehicle}</p>`;
    return;
  }

  const modelLower = handleize(normalizeToBaseModel(carMake, carModel));
  let fallbackTag = '';
  if (/^1-serie$/.test(modelLower)) fallbackTag = '1-serie';
  else if (/^2-serie$/.test(modelLower)) fallbackTag = '2-serie';
  else if (/^3-serie$/.test(modelLower)) fallbackTag = '3-serie';
  else if (/^4-serie$/.test(modelLower)) fallbackTag = '4-serie';
  else if (/^5-serie$/.test(modelLower)) fallbackTag = '5-serie';
  else if (/^6-serie$/.test(modelLower)) fallbackTag = '6-serie';
  else if (/^7-serie$/.test(modelLower)) fallbackTag = '7-serie';

  if (fallbackTag) {
    const url = `/collections/${fallbackTag}`;
    fetch(url)
      .then((r) => r.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const grid = pickProductGrid(doc);
        if (grid && productResults) productResults.innerHTML = grid.outerHTML;
        else if (productResults) productResults.innerHTML = `<p>${I18N.noVehicle}</p>`;
      })
      .catch(() => { if (productResults) productResults.innerHTML = `<p>${I18N.lookupError}</p>`; });
  } else {
    if (productResults) productResults.innerHTML = `<p>${I18N.noVehicle}</p>`;
  }
}

/* ---------------------------
  DK/SE formular (submit-handler)
---------------------------- */
function setupNumberPlateForm() {
  const form = document.getElementById('number-plate-form');
  if (!form) return;

  ensureLoaderStyles();
  const productResultsContainer = document.getElementById('product-results');
  const carInfoContainer = document.getElementById('car-info');

  const renderLoading = () => {
    if (productResultsContainer) productResultsContainer.innerHTML =
      `<div class="bm-loading" aria-busy="true"><span>${I18N.loading}</span><span class="bm-loading__spinner" aria-hidden="true"></span></div>`;
  };
  const renderError = (msg, sub) => {
    if (productResultsContainer) productResultsContainer.innerHTML =
      `<p>${msg}</p>${sub ? `<small style="opacity:.7">${sub}</small>` : ''}`;
    if (carInfoContainer) carInfoContainer.innerHTML = '';
  };

  form.addEventListener('submit', async (event) => {
    event.preventDefault();

    const numberPlateInput = document.getElementById('numberPlate');
    const plate = (numberPlateInput?.value || '').trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (!plate) { renderError(I18N.enterPlate); return; }

    const badge = form.querySelector('.country-code');
    const country = resolveCountryCode(badge); // DK/SE via badge/domæne

    renderLoading();

    try {
      if (country === 'DK') {
        const r = await fetch(`https://v1.motorapi.dk/vehicles/${encodeURIComponent(plate)}`, {
          method: 'GET',
          headers: { 'X-AUTH-TOKEN': '4fgdr35nuxc0s1wqae114duusxtzl9mv' }
        });
        if (!r.ok) throw new Error('Nummerplade ikke fundet');
        const d = await r.json();
        if (carInfoContainer) carInfoContainer.innerHTML =
          `<h4>${I18N.labelMake}: ${d.make || I18N.unknown} | ${I18N.labelModel}: ${d.model || I18N.unknown}</h4>`;
        writePlateCache('DK', plate, d.make || '', d.model || '');
        showRelevantProducts(d.make || I18N.unknown, d.model || I18N.unknown);
        return;
      }

      if (country === 'SE') {
        await handleOtherCountries(plate, 'SE'); // Worker (SE)
        return;
      }

      renderError(I18N.countryUnsupported);
    } catch (e) {
      console.error('Lookup error:', e);
      renderError(I18N.lookupError);
    }
  });
}

/* ---------------------------
  Plate-cache (nummerplade → make/model)
---------------------------- */
const PLATE_CACHE_TTL_MS = 1000 * 60 * 60 * 24 * 7; // 7 dage
function plateCacheKey(country, plate){ return `bm:plate:${country}:${plate}`; }
function readPlateCache(country, plate){
  try {
    const raw = sessionStorage.getItem(plateCacheKey(country, plate));
    if (!raw) return null;
    const { ts, make, model } = JSON.parse(raw);
    if (!make || !model || (now() - ts) > PLATE_CACHE_TTL_MS) return null;
    return { make, model };
  } catch(_) { return null; }
}
function writePlateCache(country, plate, make, model){
  try { sessionStorage.setItem(plateCacheKey(country, plate), JSON.stringify({ ts: now(), make, model })); } catch(_) {}
}

/* ---------------------------
  SE Worker fetch (retries + breaker)
---------------------------- */
const __SE_BREAKER__ = { fails: 0, openUntil: 0 };
async function workerFetchSE(numberPlate) {
  const URI = 'https://autumn-mud-ed7e.casper-3d6.workers.dev';
  const endpoint = `${URI}?number=${encodeURIComponent(numberPlate)}&country=SE`;
  if (now() < __SE_BREAKER__.openUntil) { const e = new Error('breaker-open'); e.code='BREAKER'; throw e; }

  const maxRetries = 3, perAttemptTimeout = 2000, overallDeadline = now() + 5500;
  let lastErr;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    if (now() > overallDeadline) { const e=new Error('deadline-exceeded'); e.code='DEADLINE'; throw e; }
    const jitter = Math.floor(Math.random() * 120);
    const backoff = attempt === 0 ? 0 : (260 * attempt * attempt) + jitter;
    if (backoff) await new Promise(r => setTimeout(r, backoff));
    try {
      const controller = new AbortController();
      const t = setTimeout(() => controller.abort('timeout'), perAttemptTimeout);
      const res = await fetch(endpoint, { signal: controller.signal, credentials: 'same-origin' });
      clearTimeout(t);
      if (!res.ok) { const txt = await res.text().catch(()=> ''); const err = new Error(`http-${res.status}:${txt.slice(0,120)}`); err.status = res.status; throw err; }
      const txt = await res.text();
      let data; try { data = JSON.parse(txt); } catch { throw new Error('bad-json'); }
      __SE_BREAKER__.fails = 0; __SE_BREAKER__.openUntil = 0; return data;
    } catch (e) {
      lastErr = e;
      const transient =
        e?.name === 'AbortError' || /timeout/i.test(String(e)) ||
        (e?.status && [502,503,504].includes(e.status)) || /http-502/.test(String(e)) ||
        e?.code === 'DEADLINE';
      if (!transient || attempt === maxRetries) break;
    }
  }
  __SE_BREAKER__.fails++; if (__SE_BREAKER__.fails >= 2) __SE_BREAKER__.openUntil = now() + 1000*60*2;
  throw lastErr || new Error('unknown-error');
}

/* ---------------------------
  Andre lande (SE) – via Worker (+ retries, cache, breaker)
---------------------------- */
async function handleOtherCountries(numberPlate, country) {
  ensureLoaderStyles();
  const productResultsContainer = document.getElementById('product-results');
  const carInfoContainer = document.getElementById('car-info');

  if (productResultsContainer) {
    productResultsContainer.innerHTML =
      `<div class="bm-loading" aria-busy="true"><span>${I18N.loading}</span><span class="bm-loading__spinner" aria-hidden="true"></span></div>`;
  }

  country = 'SE';

  try {
    const data = await workerFetchSE(numberPlate);

    const make =
      data?.make ||
      data?.vehicleMake ||
      data?.Marque ||
      data?.CarMake?.CurrentTextValue ||
      data?.CarMake?.Description || '';

    const model =
      data?.model ||
      data?.vehicleModel ||
      data?.Modell ||
      data?.CarModel?.CurrentTextValue ||
      data?.CarModel?.Description || '';

    if (carInfoContainer) carInfoContainer.innerHTML =
      `<h4>${I18N.labelMake}: ${make || I18N.unknown} | ${I18N.labelModel}: ${model || I18N.unknown}</h4>`;

    if (make && model) {
      writePlateCache('SE', numberPlate, make, model);
      showRelevantProducts(make, model);
    } else {
      const cached = readPlateCache('SE', numberPlate);
      if (cached) {
        if (productResultsContainer) productResultsContainer.insertAdjacentHTML('afterbegin', `<p><small>${I18N.usingCache}</small></p>`);
        if (carInfoContainer) carInfoContainer.innerHTML =
          `<h4>${I18N.labelMake}: ${cached.make || I18N.unknown} | ${I18N.labelModel}: ${cached.model || I18N.unknown}</h4>`;
        showRelevantProducts(cached.make, cached.model);
      } else {
        if (productResultsContainer) productResultsContainer.innerHTML = `<p>${I18N.noVehicle}</p>`;
        if (carInfoContainer) carInfoContainer.innerHTML = '';
      }
    }
  } catch (error) {
    console.error('Fejl i SE-opslag:', error);
    const cached = readPlateCache('SE', numberPlate);
    if (cached) {
      if (productResultsContainer) productResultsContainer.innerHTML = `<p>${I18N.usingCache}</p>`;
      if (carInfoContainer) carInfoContainer.innerHTML =
        `<h4>${I18N.labelMake}: ${cached.make || I18N.unknown} | ${I18N.labelModel}: ${cached.model || I18N.unknown}</h4>`;
      showRelevantProducts(cached.make, cached.model);
      return;
    }
    const msg = String(error?.message || error);
    const code = error?.status || error?.code || '';
    if (productResultsContainer) productResultsContainer.innerHTML =
      `<p>${I18N.lookupError}</p><small style="opacity:.7">(API-fejl ${code || 'NET'}: ${msg.replace(/^Error:\s*/, '').slice(0,160)})</small>`;
    if (carInfoContainer) carInfoContainer.innerHTML = '';
  }
}

/* ---------------------------
  Manuel mærke/model selector (uændret)
---------------------------- */
const carSelectorFormApp = {
  init() {
    this.container = document.querySelector('.car-selector-form');
    if (!this.container) return;
    this.jsonHtmlScript = this.container.querySelector('#cars-data');
    if (!this.jsonHtmlScript) return;
    try { this.carsData = JSON.parse(this.jsonHtmlScript.textContent || '{}'); } catch (_) { this.carsData = {}; }
    this.makesSelect = this.container.querySelector('#car-makes');
    this.modelsSelect = this.container.querySelector('#car-models');
    this.form = this.container;
    if (!this.makesSelect || !this.modelsSelect || !this.form) return;
    this.setupEventListeners();
  },
  setupEventListeners() {
    this.makesSelect.addEventListener('change', this.handleMakeChange.bind(this));
    this.modelsSelect.addEventListener('change', this.handleModelChange.bind(this));
    this.form.addEventListener('submit', this.handleFormSubmission.bind(this));
  },
  handleMakeChange(e) {
    const make = e.target.value;
    const models = this.carsData[make] || [];
    e.target.classList.add('selected');
    this.modelsSelect.classList.remove('selected');
    this.populateModels(models);
  },
  populateModels(models) {
    let modelOptions = `<option disabled selected value="">${this.modelsSelect.dataset.placeholder || (IS_EN ? 'Select model' : IS_SE ? 'Välj modell' : 'Vælg model')}</option>`;
    modelOptions += (models || []).map(function (model) { return `<option value="${model.handle}">${model.title}</option>`; }).join('');
    this.modelsSelect.innerHTML = modelOptions; this.modelsSelect.disabled = false;
  },
  handleModelChange(e) { e.target.classList.add('selected'); },
  handleFormSubmission(e) {
    e.preventDefault();
    const selectedMake = this.makesSelect.value;
    const selectedModel = this.modelsSelect.value; // handle/slug
    if (selectedMake && selectedModel) { showRelevantProducts(selectedMake, selectedModel); }
  }
};

/* ---------------------------
  FR locale override (valgfrit)
---------------------------- */
function overrideFRLocale() {
  try {
    if (typeof Shopify !== 'undefined' && Shopify.locale === 'fr') {
      const el = document.querySelector('.eu-flag-container .country-code');
      if (el) { el.dataset.country = 'FR'; el.textContent = 'FR'; }
    }
  } catch (_) {}
}

/* ---------------------------
  DOMContentLoaded init
---------------------------- */
document.addEventListener('DOMContentLoaded', () => {
  try { countrySelectDropdown(); } catch(_) {}
  try { setupNumberPlateForm(); } catch(_) {}
  try { carSelectorFormApp.init(); } catch(_) {}
  try { overrideFRLocale(); } catch(_) {}
});
